import React, { useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import { Application } from '@feathersjs/feathers';
import { DataOperation } from '../../app/feathers/candidate-lists/CandidateList';
import { District } from '../../app/feathers/districts/District';
import { Office } from '../../app/feathers/offices/Office';
import { Official } from '../../app/feathers/officials/Official';
import { Race } from '../../app/feathers/races/Race';
import { Measure } from '../../app/feathers/measures/Measure';
import {
  ContentType,
  ImplementationDistrict
} from '../../app/feathers/implementation-districts/ImplementationDistrict';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { useFeathers } from '../../app/util';
import { seatIdentifierTypeWritten } from '../../app/util/race-display';
import { JSONEditor } from '../../components/JSONEditor';
import { TypeDropdown } from '../../components/TypeDropdown';
import CandidateOperationsPreview, {
  CandidateOperationsPreviewCandidate,
  OfficialMatchSelection
} from './CandidateOperationsPreview';
import BuildIcon from '@mui/icons-material/Build';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface DataOperationsTableProps {
  dataOperations: DataOperation[] | null;
  title?: string;
  onChangeDataOperations?: (operations: DataOperation[]) => void;
  onChangeDataOperation?: (operation: DataOperation) => void;
  onChangeOfficialMatch?: (change: ChangeOfficialMatchPayload) => Promise<void>;
  electionKey?: string;
  editingDisabled?: boolean;
  editableJson?: boolean;
  onRetryFailedOperations?: () => void;
  defaultToOperationsTab?: boolean;
  operationsOnly?: boolean;
}

export interface ChangeOfficialMatchPayload {
  raceUniqueId: string;
  raceResolvedId?: string;
  candidateName: string;
  previousOfficial: OfficialMatchSelection;
  nextOfficial: OfficialMatchSelection;
}

type StatusType = 'all' | 'not-started' | 'completed' | 'failed' | 'no-op' | 'corrected';

interface ProgressStatus {
  label: string;
  count: number;
  color: string;
}

type OperationType = Exclude<DataOperation['operationType'], 'no-op'>;
type OverviewTab = `race-${OperationType}` | `measure-${OperationType}` | 'scouting';
const DATA_IDEMPOTENCY_FIELD = '$useIdempotencyForDataOperation';

const isOperationType = (value: unknown): value is OperationType => {
  return value === 'update' || value === 'delete' || value === 'create';
};

const getOverviewOperationType = (operation: DataOperation): OperationType | null => {
  if (operation.operationType === 'no-op') return 'update';
  return isOperationType(operation.operationType) ? operation.operationType : null;
};

const getOperationText = (operationType: DataOperation['operationType']): string => {
  if (operationType === 'create') return 'Create';
  if (operationType === 'delete') return 'Delete';
  if (operationType === 'no-op') return 'No-op';
  return 'Update';
};

const getPositionDisplayText = (positionData?: Race['positionData']): string => {
  const seatIdentifierType = seatIdentifierTypeWritten(positionData?.seatIdentifierType);
  const positionText = [seatIdentifierType, positionData?.seatIdentifier].filter(Boolean).join(' ');
  return [positionText, positionData?.special ? '(special)' : null].filter(Boolean).join(' ');
};

const clipWords = (text: string, wordLimit: number): string => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= wordLimit) return text;
  return `${words.slice(0, wordLimit).join(' ')}…`;
};

const getExistingOfficialKey = (operation: DataOperation): string | null => {
  const official = operation.data?.official;
  if (official?.$create) return null;
  if (typeof official === 'string') return official;
  if (official?.key || official?._id) return official.key || official._id;
  if (operation.entityType === 'candidates') {
    return operation.uniqueId;
  }
  return null;
};

const getOfficialMatch = (operation: DataOperation): OfficialMatchSelection => {
  return operation.data?.official?.$create === true
    ? { $create: true }
    : getExistingOfficialKey(operation) || operation.uniqueId;
};

const candidatePreviewFromOperation = (
  operation: DataOperation,
  official?: Official | null
): CandidateOperationsPreviewCandidate => {
  const recentCandidate = official?.recentCandidate;
  return {
    id: operation.resolvedId || operation.uniqueId,
    name: operation.data?.name ||
      recentCandidate?.stagingDraft?.name ||
      recentCandidate?.name ||
      official?.name ||
      operation.uniqueId,
    party: recentCandidate?.party || operation.data?.party,
    photoPath: recentCandidate?.stagingDraft?.photoPathFace ||
      recentCandidate?.photoPathFace ||
      operation.data?.photoPathFace,
    hasVariants: (
      recentCandidate?.stagingDraft?.photoPathFace
        ? recentCandidate?.stagingDraft?.hasVariants
        : recentCandidate?.hasVariants
    ) === true,
    createAsNewOfficial: operation.data?.official?.$create === true,
    officialMatch: getOfficialMatch(operation)
  };
};

const addOfficialMatchEditing = (
  candidate: CandidateOperationsPreviewCandidate,
  candidateOperation: DataOperation,
  raceOperation: DataOperation,
  onChangeOfficialMatch?: (change: ChangeOfficialMatchPayload) => Promise<void>
): CandidateOperationsPreviewCandidate => {
  if (
    !onChangeOfficialMatch ||
    !['create', 'update', 'no-op'].includes(candidateOperation.operationType) ||
    !['not-started', 'no-op'].includes(candidateOperation.status)
  ) return candidate;

  const previousOfficial = getOfficialMatch(candidateOperation);
  return {
    ...candidate,
    onChangeOfficialMatch: nextMatch => onChangeOfficialMatch({
      raceUniqueId: raceOperation.uniqueId,
      ...(raceOperation.resolvedId ? { raceResolvedId: raceOperation.resolvedId } : {}),
      candidateName: candidate.name,
      previousOfficial,
      nextOfficial: nextMatch
    })
  };
};

const ENTITY_QUERY_CHUNK_SIZE = 30;

async function loadEntitiesByChunkedValues<T>(
  values: string[],
  loadChunk: (chunk: string[]) => Promise<T[]>
): Promise<T[]> {
  const entities: T[] = [];
  for (let index = 0; index < values.length; index += ENTITY_QUERY_CHUNK_SIZE) {
    const chunk = values.slice(index, index + ENTITY_QUERY_CHUNK_SIZE);
    entities.push(...await loadChunk(chunk));
  }
  return entities;
}

interface RaceOperationGroup {
  raceOperation: DataOperation;
  overviewType: OperationType;
  candidateOperations: DataOperation[];
}

interface OverviewOperationGroup {
  raceGroups: RaceOperationGroup[];
  unmatchedCandidateOperations: DataOperation[];
  operationCount: number;
}

type DeleteOverviewOperations = (operations: DataOperation[]) => void;

const OverviewDeleteButton: React.FC<{
  operations: DataOperation[];
  onDeleteOperations?: DeleteOverviewOperations;
}> = ({ operations, onDeleteOperations }) => {
  if (!onDeleteOperations || operations.some(operation => operation.status === 'completed')) {
    return null;
  }

  return (
    <Tooltip title={operations.length === 1 ? 'Delete operation' : `Delete ${operations.length} linked operations`}>
      <IconButton
        size="small"
        color="error"
        aria-label={operations.length === 1 ? 'Delete operation' : `Delete ${operations.length} linked operations`}
        onClick={() => onDeleteOperations(operations)}
      >
        <DeleteIcon style={{ fontSize: '16px' }} />
      </IconButton>
    </Tooltip>
  );
};

type RaceUpdateSection = 'candidate-deletes' | 'candidate-creates' | 'candidate-updates';

const RACE_UPDATE_SECTIONS: Record<
  RaceUpdateSection,
  { label: string; priority: number }
> = {
  'candidate-deletes': { label: 'Candidate Deletes', priority: 0 },
  'candidate-creates': { label: 'Candidate Creates', priority: 1 },
  'candidate-updates': { label: 'Candidate Updates', priority: 2 }
};

const getDuplicateRaceOperationCounts = (
  raceGroups: RaceOperationGroup[]
): Map<string, number> => {
  const operationCountByRaceId = new Map<string, number>();
  raceGroups.forEach(({ raceOperation }) => {
    if (!raceOperation.resolvedId) return;
    operationCountByRaceId.set(
      raceOperation.resolvedId,
      (operationCountByRaceId.get(raceOperation.resolvedId) || 0) + 1
    );
  });

  return new Map(
    [...operationCountByRaceId.entries()]
      .filter(([, operationCount]) => operationCount > 1)
  );
};

const DuplicateRaceMappingErrors: React.FC<{
  duplicateRaceOperationCounts: Map<string, number>;
}> = ({ duplicateRaceOperationCounts }) => {
  return (
    <>
      {[...duplicateRaceOperationCounts.entries()]
        .map(([raceId, operationCount]) => (
          <Typography key={raceId} variant="body2" color="error" style={{ marginBottom: '12px' }}>
            {operationCount} race operations map to the same race ID: {raceId}.
          </Typography>
        ))}
    </>
  );
};

const DuplicateRaceMappingIcon: React.FC<{
  raceId: string;
  duplicateRaceOperationCounts: Map<string, number>;
}> = ({ raceId, duplicateRaceOperationCounts }) => {
  const operationCount = duplicateRaceOperationCounts.get(raceId);
  if (!operationCount) return null;

  return (
    <Tooltip title={`${operationCount} race operations map to this race ID`}>
      <ErrorOutlineIcon
        color="error"
        fontSize="small"
        aria-label={`Duplicate race mapping for ${raceId}`}
      />
    </Tooltip>
  );
};

const RACES_PER_PAGE = 25;
const MEASURES_PER_PAGE = 25;
const SCOUTING_DISTRICTS_PER_PAGE = 25;

interface FixOperationPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onConfirm: (resolvedId: string) => void;
}

const isValidObjectId = (id: string): boolean => {
  return /^[0-9a-fA-F]{24}$/.test(id);
};

const OperationsProgress: React.FC<{ dataOperations: DataOperation[] }> = ({ dataOperations }) => {
  const totalOperations = dataOperations.length;
  const progressStatuses: ProgressStatus[] = [
    {
      label: 'Success',
      count: dataOperations.filter(operation => ['completed', 'no-op'].includes(operation.status)).length,
      color: '#4caf7d'
    },
    {
      label: 'Failed',
      count: dataOperations.filter(operation => operation.status === 'failed').length,
      color: '#e45b62'
    },
    {
      label: 'Pending',
      count: dataOperations.filter(operation => operation.status === 'not-started').length,
      color: '#9ca3af'
    },
    {
      label: 'Resolved',
      count: dataOperations.filter(operation => operation.status === 'corrected').length,
      color: '#9bd3a5'
    }
  ];

  return (
    <div style={{ marginBottom: '24px' }}>
      <Typography
        variant="overline"
        style={{ display: 'block', color: '#4b4f5c', fontWeight: 700, letterSpacing: '0.7px', marginBottom: '8px' }}
      >
        Processing Progress
      </Typography>
      <div
        role="progressbar"
        aria-label="Data operations processing progress"
        aria-valuemin={0}
        aria-valuemax={totalOperations}
        aria-valuenow={progressStatuses[0].count + progressStatuses[3].count}
        style={{
          display: 'flex',
          width: '100%',
          height: '8px',
          overflow: 'hidden',
          backgroundColor: '#e5e7eb',
          borderRadius: '4px',
          marginBottom: '14px'
        }}
      >
        {progressStatuses.map(status => (
          <div
            key={status.label}
            title={`${status.label}: ${status.count}`}
            style={{
              width: `${(status.count / totalOperations) * 100}%`,
              backgroundColor: status.color
            }}
          />
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px' }}>
        {progressStatuses.map(status => (
          <div key={status.label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px' }}>
              <span
                aria-hidden="true"
                style={{ width: '7px', height: '7px', flexShrink: 0, borderRadius: '50%', backgroundColor: status.color }}
              />
              <Typography variant="body2" style={{ color: status.color, fontWeight: 600 }}>
                {status.label}
              </Typography>
            </div>
            <Typography variant="h6" style={{ color: '#172033', fontWeight: 700, lineHeight: 1.2 }}>
              {status.count.toLocaleString()}
            </Typography>
            <Typography variant="body2" style={{ color: '#5f6673' }}>
              {Math.round((status.count / totalOperations) * 100)}%
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};

const RaceCreatesTable: React.FC<{
  raceGroups: RaceOperationGroup[];
  onChangeOfficialMatch?: (change: ChangeOfficialMatchPayload) => Promise<void>;
  onDeleteOperations?: DeleteOverviewOperations;
}> = ({ raceGroups, onChangeOfficialMatch, onDeleteOperations }) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [districtsById, setDistrictsById] = useState<Map<string, District>>(new Map());
  const [officesByKey, setOfficesByKey] = useState<Map<string, Office>>(new Map());
  const [officialsByKey, setOfficialsByKey] = useState<Map<string, Official>>(new Map());
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingOffices, setLoadingOffices] = useState(false);
  const [loadingOfficials, setLoadingOfficials] = useState(false);
  const [districtLoadFailed, setDistrictLoadFailed] = useState(false);
  const [officeLoadFailed, setOfficeLoadFailed] = useState(false);
  const [officialLoadFailed, setOfficialLoadFailed] = useState(false);
  const [page, setPage] = useState(1);

  const districtIds = useMemo(() => {
    return [...new Set(raceGroups
      .map(({ raceOperation }) => raceOperation.data?.district)
      .map(district => `${district?._id || district || ''}`)
      .filter(Boolean))];
  }, [raceGroups]);

  const officeKeys = useMemo(() => {
    return [...new Set(raceGroups
      .map(({ raceOperation }) => `${raceOperation.data?.office || ''}`)
      .filter(Boolean))];
  }, [raceGroups]);

  const officialKeys = useMemo(() => {
    return [...new Set(raceGroups
      .flatMap(({ candidateOperations }) => candidateOperations)
      .map(getExistingOfficialKey)
      .filter((key): key is string => Boolean(key)))];
  }, [raceGroups]);

  useEffect(() => {
    setPage(1);
  }, [raceGroups]);

  useEffect(() => {
    let cancelled = false;

    const loadDistricts = async () => {
      if (!feathers || districtIds.length === 0) {
        setDistrictsById(new Map());
        setDistrictLoadFailed(false);
        setLoadingDistricts(false);
        return;
      }

      setLoadingDistricts(true);
      setDistrictLoadFailed(false);
      try {
        const districts = await loadEntitiesByChunkedValues<District>(
          districtIds,
          async chunk => {
            const response = await feathers.getService('districts').find({
              query: { _id: chunk, $limit: ENTITY_QUERY_CHUNK_SIZE }
            });
            return response.data;
          }
        );
        if (cancelled) return;
        setDistrictsById(new Map((districts || []).map(district => [`${district._id}`, district])));
      } catch {
        if (cancelled) return;
        setDistrictsById(new Map());
        setDistrictLoadFailed(true);
      } finally {
        if (cancelled) return;
        setLoadingDistricts(false);
      }
    };

    loadDistricts();
    return () => {
      cancelled = true;
    };
  }, [districtIds, feathers]);

  useEffect(() => {
    let cancelled = false;

    const loadOffices = async () => {
      if (!feathers || officeKeys.length === 0) {
        setOfficesByKey(new Map());
        setOfficeLoadFailed(false);
        setLoadingOffices(false);
        return;
      }

      setLoadingOffices(true);
      setOfficeLoadFailed(false);
      try {
        const offices = await loadEntitiesByChunkedValues<Office>(
          officeKeys,
          async chunk => {
            const response = await feathers.getService('offices').find({
              query: { key: chunk, $limit: ENTITY_QUERY_CHUNK_SIZE }
            });
            return response.data;
          }
        );
        if (cancelled) return;
        setOfficesByKey(new Map(offices.map(office => [office.key, office])));
      } catch {
        if (cancelled) return;
        setOfficesByKey(new Map());
        setOfficeLoadFailed(true);
      } finally {
        if (cancelled) return;
        setLoadingOffices(false);
      }
    };

    loadOffices();
    return () => {
      cancelled = true;
    };
  }, [officeKeys, feathers]);

  useEffect(() => {
    let cancelled = false;

    const loadOfficials = async () => {
      if (!feathers || officialKeys.length === 0) {
        setOfficialsByKey(new Map());
        setOfficialLoadFailed(false);
        setLoadingOfficials(false);
        return;
      }

      setLoadingOfficials(true);
      setOfficialLoadFailed(false);
      try {
        const officials = await loadEntitiesByChunkedValues<Official>(
          officialKeys,
          async chunk => {
            const response = await feathers.getService('officials').find({
              query: { key: chunk, $limit: ENTITY_QUERY_CHUNK_SIZE }
            });
            return response.data;
          }
        );
        if (cancelled) return;
        const nextOfficialsByKey = new Map(officials.map(official => [official.key, official]));
        setOfficialsByKey(nextOfficialsByKey);
      } catch {
        if (cancelled) return;
        setOfficialsByKey(new Map());
        setOfficialLoadFailed(true);
      } finally {
        if (cancelled) return;
        setLoadingOfficials(false);
      }
    };

    loadOfficials();
    return () => {
      cancelled = true;
    };
  }, [officialKeys, feathers]);

  const pageCount = Math.ceil(raceGroups.length / RACES_PER_PAGE);
  const visibleRaceGroups = raceGroups.slice((page - 1) * RACES_PER_PAGE, page * RACES_PER_PAGE);
  const firstVisibleRace = raceGroups.length === 0 ? 0 : (page - 1) * RACES_PER_PAGE + 1;
  const lastVisibleRace = Math.min(page * RACES_PER_PAGE, raceGroups.length);

  if (raceGroups.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', backgroundColor: '#fafafa', borderRadius: '6px' }}>
        <Typography variant="body2" color="textSecondary">
          No races will be created.
        </Typography>
      </div>
    );
  }

  return (
    <div>
      {districtLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          District names could not be loaded. District IDs are shown instead.
        </Typography>
      )}
      {officeLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          Office names could not be loaded. Office keys are shown instead.
        </Typography>
      )}
      {officialLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          Existing official photos could not be loaded.
        </Typography>
      )}
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
          <thead style={{ backgroundColor: '#f7f8fa' }}>
            <tr>
              <th style={{ padding: '12px 16px', textAlign: 'left', width: '38%', color: '#4b5563' }}>Race</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', width: '24%', color: '#4b5563' }}>Position</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', width: '30%', color: '#4b5563' }}>Candidates</th>
              <th aria-label="Actions" style={{ padding: '12px 16px', width: '8%' }} />
            </tr>
          </thead>
          <tbody>
            {visibleRaceGroups.map(({ raceOperation, candidateOperations }) => {
              const raceData = raceOperation.data || {};
              const districtId = `${raceData.district?._id || raceData.district || ''}`;
              const district = districtsById.get(districtId);
              const districtName = district?.longName || district?.displayName || districtId || 'Unknown district';
              const officeKey = `${raceData.office || ''}`;
              const officeName = officesByKey.get(officeKey)?.name || officeKey || 'Unknown office';
              const party = raceData.raceParty || raceData.party;
              const positionText = getPositionDisplayText(raceData.positionData);
              const candidates = candidateOperations.map(operation => {
                  const officialKey = getExistingOfficialKey(operation);
                  const official = officialKey ? officialsByKey.get(officialKey) : null;
                  return addOfficialMatchEditing(
                    candidatePreviewFromOperation(operation, official),
                    operation,
                    raceOperation,
                    onChangeOfficialMatch
                  );
                });
              const newOfficialCount = candidateOperations.filter(
                operation => operation.data?.official?.$create === true
              ).length;

              return (
                <tr key={raceOperation.uniqueId} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '18px 16px', verticalAlign: 'middle' }}>
                    <Typography variant="subtitle1" style={{ color: '#172033', fontWeight: 600 }}>
                      {loadingOffices && !officesByKey.has(officeKey) ? 'Loading office…' : officeName}
                      {party ? ` (${party})` : ''}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {loadingDistricts && !district ? 'Loading district…' : districtName}
                    </Typography>
                  </td>
                  <td style={{ padding: '18px 16px', verticalAlign: 'middle' }}>
                    <Typography variant="body2" style={{ color: '#374151' }}>
                      {positionText || '--'}
                    </Typography>
                  </td>
                  <td style={{ padding: '18px 16px', verticalAlign: 'middle' }}>
                    <CandidateOperationsPreview
                      candidates={candidates}
                      detail={`Create ${newOfficialCount} as new`}
                    />
                  </td>
                  <td style={{ padding: '18px 16px', textAlign: 'right', verticalAlign: 'middle' }}>
                    <OverviewDeleteButton
                      operations={[raceOperation, ...candidateOperations]}
                      onDeleteOperations={onDeleteOperations}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
        <Typography variant="body2" color="textSecondary">
          Showing {firstVisibleRace}–{lastVisibleRace} of {raceGroups.length} races
        </Typography>
        {pageCount > 1 && (
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="small"
          />
        )}
      </div>
      {(loadingDistricts || loadingOffices || loadingOfficials) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="textSecondary">
            Loading race details
          </Typography>
        </div>
      )}
    </div>
  );
};

const RaceDeletesTable: React.FC<{
  raceGroups: RaceOperationGroup[];
  electionKey?: string;
  onDeleteOperations?: DeleteOverviewOperations;
}> = ({ raceGroups, electionKey, onDeleteOperations }) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [racesById, setRacesById] = useState<Map<string, Race>>(new Map());
  const [loadingRaces, setLoadingRaces] = useState(false);
  const [raceLoadFailed, setRaceLoadFailed] = useState(false);
  const [page, setPage] = useState(1);

  const raceIds = useMemo(() => {
    return [...new Set(raceGroups
      .map(({ raceOperation }) => raceOperation.resolvedId)
      .filter((id): id is string => Boolean(id)))];
  }, [raceGroups]);

  useEffect(() => {
    setPage(1);
  }, [raceGroups]);

  const duplicateRaceOperationCounts = useMemo(
    () => getDuplicateRaceOperationCounts(raceGroups),
    [raceGroups]
  );

  useEffect(() => {
    let cancelled = false;

    const loadRaces = async () => {
      if (!feathers || raceIds.length === 0) {
        setRacesById(new Map());
        setRaceLoadFailed(false);
        setLoadingRaces(false);
        return;
      }

      setLoadingRaces(true);
      setRaceLoadFailed(false);
      try {
        const races = await loadEntitiesByChunkedValues<Race>(
          raceIds,
          async chunk => {
            const response = await feathers.getService('races').find({
              query: { _id: chunk, $limit: ENTITY_QUERY_CHUNK_SIZE }
            });
            return response.data;
          }
        );
        if (cancelled) return;
        setRacesById(new Map(races.map(race => [`${race._id}`, race])));
      } catch {
        if (cancelled) return;
        setRacesById(new Map());
        setRaceLoadFailed(true);
      } finally {
        if (!cancelled) setLoadingRaces(false);
      }
    };

    loadRaces();
    return () => {
      cancelled = true;
    };
  }, [raceIds, feathers]);

  const orderedRaces = [...raceIds]
    .sort((firstId, secondId) => (
      Number(duplicateRaceOperationCounts.has(secondId)) -
      Number(duplicateRaceOperationCounts.has(firstId))
    ))
    .map(raceId => racesById.get(raceId))
    .filter((race): race is Race => Boolean(race));
  const pageCount = Math.ceil(orderedRaces.length / RACES_PER_PAGE);
  const visibleRaces = orderedRaces.slice((page - 1) * RACES_PER_PAGE, page * RACES_PER_PAGE);
  const firstVisibleRace = orderedRaces.length === 0 ? 0 : (page - 1) * RACES_PER_PAGE + 1;
  const lastVisibleRace = Math.min(page * RACES_PER_PAGE, orderedRaces.length);
  const missingRaceCount = raceGroups.filter(({ raceOperation }) => (
    !raceOperation.resolvedId || !racesById.has(raceOperation.resolvedId)
  )).length;

  if (raceGroups.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', backgroundColor: '#fafafa', borderRadius: '6px' }}>
        <Typography variant="body2" color="textSecondary">
          No races will be deleted.
        </Typography>
      </div>
    );
  }

  if (loadingRaces && orderedRaces.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', padding: '40px' }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          Loading races
        </Typography>
      </div>
    );
  }

  return (
    <div>
      {raceLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          Races marked for deletion could not be loaded.
        </Typography>
      )}
      {!raceLoadFailed && missingRaceCount > 0 && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          {missingRaceCount} race{missingRaceCount === 1 ? '' : 's'} could not be found.
        </Typography>
      )}
      <DuplicateRaceMappingErrors duplicateRaceOperationCounts={duplicateRaceOperationCounts} />
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
          <thead style={{ backgroundColor: '#f7f8fa' }}>
            <tr>
              <th style={{ padding: '12px 16px', textAlign: 'left', width: '34%', color: '#4b5563' }}>Race</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', width: '22%', color: '#4b5563' }}>Position</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', width: '36%', color: '#4b5563' }}>Candidates</th>
              <th aria-label="Actions" style={{ padding: '12px 16px', width: '8%' }} />
            </tr>
          </thead>
          <tbody>
            {visibleRaces.map(race => {
              const officeValue = race.office as unknown;
              const officeName = typeof officeValue === 'string'
                ? race.officeName || officeValue
                : (officeValue as Office)?.name || race.officeName || 'Unknown office';
              const districtValue = race.district as unknown;
              const districtName = typeof districtValue === 'string'
                ? districtValue
                : (districtValue as District)?.longName ||
                  (districtValue as District)?.displayName ||
                  'Unknown district';
              const positionText = getPositionDisplayText(race.positionData);
              const candidateCount = race.candidateSummary?.numCandidates || 0;
              const operationsForRace = raceGroups
                .filter(({ raceOperation }) => raceOperation.resolvedId === `${race._id}`)
                .flatMap(({ raceOperation, candidateOperations }) => [
                  raceOperation,
                  ...candidateOperations
                ]);
              const candidates = Array.from({ length: candidateCount }, (_, index) => ({
                id: `${race.candidateSummary?.ids?.[index] || race.candidateSummary?.keys?.[index] || index}`,
                name: race.candidateSummary?.names?.[index] ||
                  race.candidateSummary?.keys?.[index] ||
                  `Candidate ${index + 1}`,
                party: race.candidateSummary?.parties?.[index],
                photoPath: race.candidateSummary?.photos?.[index]
              }));

              return (
                <tr key={race._id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '18px 16px', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <DuplicateRaceMappingIcon
                        raceId={`${race._id}`}
                        duplicateRaceOperationCounts={duplicateRaceOperationCounts}
                      />
                      <Typography variant="subtitle1" style={{ color: '#172033', fontWeight: 600 }}>
                        {officeName}{race.party ? ` (${race.party})` : ''}
                      </Typography>
                    </div>
                    <Typography variant="body2" color="textSecondary">
                      {districtName}
                    </Typography>
                  </td>
                  <td style={{ padding: '18px 16px', verticalAlign: 'middle' }}>
                    <Typography variant="body2" style={{ color: '#374151' }}>
                      {positionText || '--'}
                    </Typography>
                  </td>
                  <td style={{ padding: '18px 16px', verticalAlign: 'middle' }}>
                    <CandidateOperationsPreview candidates={candidates} />
                  </td>
                  <td style={{ padding: '18px 16px', textAlign: 'right', verticalAlign: 'middle' }}>
                    {electionKey && (
                      <Tooltip title="Open race">
                        <IconButton
                          component="a"
                          href={`/elections/${electionKey}/races/${race._id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${officeName} race`}
                          size="small"
                          color="primary"
                        >
                          <ArrowForwardIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <OverviewDeleteButton
                      operations={operationsForRace}
                      onDeleteOperations={onDeleteOperations}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
        <Typography variant="body2" color="textSecondary">
          Showing {firstVisibleRace}–{lastVisibleRace} of {orderedRaces.length} races
        </Typography>
        {pageCount > 1 && (
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="small"
          />
        )}
      </div>
    </div>
  );
};

const RaceUpdatesTable: React.FC<{
  raceGroups: RaceOperationGroup[];
  unmatchedCandidateOperations: DataOperation[];
  electionKey?: string;
  onChangeOfficialMatch?: (change: ChangeOfficialMatchPayload) => Promise<void>;
  onDeleteOperations?: DeleteOverviewOperations;
}> = ({
  raceGroups,
  unmatchedCandidateOperations,
  electionKey,
  onChangeOfficialMatch,
  onDeleteOperations
}) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [racesById, setRacesById] = useState<Map<string, Race>>(new Map());
  const [officialsByKey, setOfficialsByKey] = useState<Map<string, Official>>(new Map());
  const [loadingRaces, setLoadingRaces] = useState(false);
  const [loadingOfficials, setLoadingOfficials] = useState(false);
  const [raceLoadFailed, setRaceLoadFailed] = useState(false);
  const [officialLoadFailed, setOfficialLoadFailed] = useState(false);
  const [page, setPage] = useState(1);

  const raceIds = useMemo(() => {
    return [...new Set(raceGroups
      .map(({ raceOperation }) => raceOperation.resolvedId)
      .filter((id): id is string => Boolean(id)))];
  }, [raceGroups]);

  const officialKeys = useMemo(() => {
    return [...new Set(raceGroups
      .flatMap(({ candidateOperations }) => candidateOperations)
      .filter(operation => operation.operationType === 'create')
      .map(getExistingOfficialKey)
      .filter((key): key is string => Boolean(key)))];
  }, [raceGroups]);

  const duplicateRaceOperationCounts = useMemo(
    () => getDuplicateRaceOperationCounts(raceGroups),
    [raceGroups]
  );

  useEffect(() => {
    setPage(1);
  }, [raceGroups]);

  useEffect(() => {
    let cancelled = false;

    const loadRaces = async () => {
      if (!feathers || raceIds.length === 0) {
        setRacesById(new Map());
        setRaceLoadFailed(false);
        setLoadingRaces(false);
        return;
      }

      setLoadingRaces(true);
      setRaceLoadFailed(false);
      try {
        const races = await loadEntitiesByChunkedValues<Race>(
          raceIds,
          async chunk => {
            const response = await feathers.getService('races').find({
              query: { _id: chunk, $limit: ENTITY_QUERY_CHUNK_SIZE }
            });
            return response.data;
          }
        );
        if (cancelled) return;
        setRacesById(new Map(races.map(race => [`${race._id}`, race])));
      } catch {
        if (cancelled) return;
        setRacesById(new Map());
        setRaceLoadFailed(true);
      } finally {
        if (!cancelled) setLoadingRaces(false);
      }
    };

    loadRaces();
    return () => {
      cancelled = true;
    };
  }, [raceIds, feathers]);

  useEffect(() => {
    let cancelled = false;

    const loadOfficials = async () => {
      if (!feathers || officialKeys.length === 0) {
        setOfficialsByKey(new Map());
        setOfficialLoadFailed(false);
        setLoadingOfficials(false);
        return;
      }

      setLoadingOfficials(true);
      setOfficialLoadFailed(false);
      try {
        const officials = await loadEntitiesByChunkedValues<Official>(
          officialKeys,
          async chunk => {
            const response = await feathers.getService('officials').find({
              query: { key: chunk, $limit: ENTITY_QUERY_CHUNK_SIZE }
            });
            return response.data;
          }
        );
        if (cancelled) return;
        setOfficialsByKey(new Map(officials.map(official => [official.key, official])));
      } catch {
        if (cancelled) return;
        setOfficialsByKey(new Map());
        setOfficialLoadFailed(true);
      } finally {
        if (!cancelled) setLoadingOfficials(false);
      }
    };

    loadOfficials();
    return () => {
      cancelled = true;
    };
  }, [officialKeys, feathers]);

  const groupsByRaceId = new Map(
    raceGroups
      .filter(({ raceOperation }) => Boolean(raceOperation.resolvedId))
      .map(group => [group.raceOperation.resolvedId as string, group])
  );
  const rows = [...raceIds]
    .map(raceId => {
      const race = racesById.get(raceId);
      const group = groupsByRaceId.get(raceId);
      if (!race || !group) return null;

      const summary = race.candidateSummary;
      const unmatchedCandidatesForRace = unmatchedCandidateOperations.filter(operation => (
        summary?.ids?.some(id => `${id}` === `${operation.resolvedId}`) ||
        summary?.keys?.includes(operation.uniqueId)
      ));
      const candidateOperations = [
        ...group.candidateOperations,
        ...unmatchedCandidatesForRace
      ];
      const section: RaceUpdateSection = candidateOperations.some(
        operation => operation.operationType === 'delete'
      )
        ? 'candidate-deletes'
        : candidateOperations.some(operation => operation.operationType === 'create')
          ? 'candidate-creates'
          : 'candidate-updates';

      return { race, group, unmatchedCandidatesForRace, section };
    })
    .filter((row): row is {
      race: Race;
      group: RaceOperationGroup;
      unmatchedCandidatesForRace: DataOperation[];
      section: RaceUpdateSection;
    } => Boolean(row))
    .sort((firstRow, secondRow) => (
      RACE_UPDATE_SECTIONS[firstRow.section].priority -
      RACE_UPDATE_SECTIONS[secondRow.section].priority ||
      Number(duplicateRaceOperationCounts.has(`${secondRow.race._id}`)) -
      Number(duplicateRaceOperationCounts.has(`${firstRow.race._id}`))
    ));
  const pageCount = Math.ceil(rows.length / RACES_PER_PAGE);
  const visibleRows = rows.slice((page - 1) * RACES_PER_PAGE, page * RACES_PER_PAGE);
  const firstVisibleRace = rows.length === 0 ? 0 : (page - 1) * RACES_PER_PAGE + 1;
  const lastVisibleRace = Math.min(page * RACES_PER_PAGE, rows.length);
  const sectionCounts = rows.reduce<Record<RaceUpdateSection, number>>((counts, row) => ({
    ...counts,
    [row.section]: counts[row.section] + 1
  }), {
    'candidate-deletes': 0,
    'candidate-creates': 0,
    'candidate-updates': 0
  });
  const missingRaceCount = raceGroups.filter(({ raceOperation }) => (
    !raceOperation.resolvedId || !racesById.has(raceOperation.resolvedId)
  )).length;

  if (raceGroups.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', backgroundColor: '#fafafa', borderRadius: '6px' }}>
        <Typography variant="body2" color="textSecondary">
          No races will be updated.
        </Typography>
      </div>
    );
  }

  if (loadingRaces && rows.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', padding: '40px' }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          Loading races
        </Typography>
      </div>
    );
  }

  return (
    <div>
      {raceLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          Races marked for update could not be loaded.
        </Typography>
      )}
      {officialLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          Existing official photos could not be loaded.
        </Typography>
      )}
      {!raceLoadFailed && missingRaceCount > 0 && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          {missingRaceCount} race{missingRaceCount === 1 ? '' : 's'} could not be found.
        </Typography>
      )}
      <DuplicateRaceMappingErrors duplicateRaceOperationCounts={duplicateRaceOperationCounts} />
      <div style={{ maxHeight: '70vh', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1180px' }}>
          <thead style={{
            backgroundColor: '#f7f8fa',
            position: 'sticky',
            top: 0,
            zIndex: 2,
            boxShadow: '0 1px 0 #e5e7eb'
          }}>
            <tr>
              <th style={{ padding: '12px', textAlign: 'left', width: '24%', color: '#4b5563' }}>Race</th>
              <th style={{ padding: '12px', textAlign: 'left', width: '14%', color: '#4b5563' }}>Position</th>
              <th style={{ padding: '12px', textAlign: 'left', width: '18%', color: '#4b5563' }}>Candidate creates</th>
              <th style={{ padding: '12px', textAlign: 'left', width: '18%', color: '#4b5563' }}>Candidate updates</th>
              <th style={{ padding: '12px', textAlign: 'left', width: '18%', color: '#4b5563' }}>Candidate deletes</th>
              <th aria-label="Actions" style={{ padding: '12px', width: '8%' }} />
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(({ race, group, unmatchedCandidatesForRace, section }, index) => {
              const officeValue = race.office as unknown;
              const officeName = typeof officeValue === 'string'
                ? race.officeName || officeValue
                : (officeValue as Office)?.name || race.officeName || 'Unknown office';
              const districtValue = race.district as unknown;
              const districtName = typeof districtValue === 'string'
                ? districtValue
                : (districtValue as District)?.longName ||
                  (districtValue as District)?.displayName ||
                  'Unknown district';
              const positionText = getPositionDisplayText(race.positionData);
              const summary = race.candidateSummary;
              const candidateCreates = group.candidateOperations.filter(operation => operation.operationType === 'create');
              const candidateUpdates = [
                ...group.candidateOperations,
                ...unmatchedCandidatesForRace
              ].filter(operation => operation.operationType === 'update' || operation.operationType === 'no-op');
              const candidateDeletes = [
                ...group.candidateOperations,
                ...unmatchedCandidatesForRace
              ].filter(operation => operation.operationType === 'delete');
              const createCandidates = candidateCreates.map(operation => {
                const officialKey = getExistingOfficialKey(operation);
                const official = officialKey ? officialsByKey.get(officialKey) : null;
                return addOfficialMatchEditing(
                  candidatePreviewFromOperation(operation, official),
                  operation,
                  group.raceOperation,
                  onChangeOfficialMatch
                );
              });
              const summaryCandidateForOperation = (
                operation: DataOperation
              ): CandidateOperationsPreviewCandidate => {
                const idIndex = summary?.ids?.findIndex(id => `${id}` === `${operation.resolvedId}`) ?? -1;
                const keyIndex = summary?.keys?.findIndex(key => key === operation.uniqueId) ?? -1;
                const index = idIndex >= 0 ? idIndex : keyIndex;
                return {
                  id: operation.resolvedId || operation.uniqueId,
                  name: operation.data?.name ||
                    (index >= 0 ? summary?.names?.[index] : null) ||
                    operation.uniqueId,
                  party: (index >= 0 ? summary?.parties?.[index] : null) || operation.data?.party,
                  photoPath: index >= 0 ? summary?.photos?.[index] : operation.data?.photoPathFace,
                  hasVariants: index >= 0 && summary?.photoHasVariants?.[index] === true,
                };
              };

              return (
                <React.Fragment key={race._id}>
                  {(index === 0 || visibleRows[index - 1].section !== section) && (
                    <tr>
                      <th
                        colSpan={6}
                        scope="colgroup"
                        style={{
                          backgroundColor: '#eef1f5',
                          color: '#374151',
                          padding: '9px 12px',
                          position: 'sticky',
                          top: '45px',
                          zIndex: 1,
                          textAlign: 'left',
                          boxShadow: '0 1px 0 #d1d5db'
                        }}
                      >
                        {RACE_UPDATE_SECTIONS[section].label} ({sectionCounts[section]})
                      </th>
                    </tr>
                  )}
                  <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '18px 12px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <DuplicateRaceMappingIcon
                          raceId={`${race._id}`}
                          duplicateRaceOperationCounts={duplicateRaceOperationCounts}
                        />
                        <Typography variant="subtitle1" style={{ color: '#172033', fontWeight: 600 }}>
                          {officeName}{race.party ? ` (${race.party})` : ''}
                        </Typography>
                      </div>
                      <Typography variant="body2" color="textSecondary">
                        {districtName}
                      </Typography>
                    </td>
                    <td style={{ padding: '18px 12px', verticalAlign: 'middle' }}>
                      <Typography variant="body2" style={{ color: '#374151' }}>
                        {positionText || '--'}
                      </Typography>
                    </td>
                    <td style={{ padding: '18px 12px', verticalAlign: 'middle' }}>
                      <CandidateOperationsPreview candidates={createCandidates} />
                    </td>
                    <td style={{ padding: '18px 12px', verticalAlign: 'middle' }}>
                      <CandidateOperationsPreview
                        candidates={candidateUpdates.map(operation => addOfficialMatchEditing(
                          summaryCandidateForOperation(operation),
                          operation,
                          group.raceOperation,
                          onChangeOfficialMatch
                        ))}
                      />
                    </td>
                    <td style={{ padding: '18px 12px', verticalAlign: 'middle' }}>
                      <CandidateOperationsPreview
                        candidates={candidateDeletes.map(summaryCandidateForOperation)}
                      />
                    </td>
                    <td style={{ padding: '18px 12px', textAlign: 'right', verticalAlign: 'middle' }}>
                      {electionKey && (
                        <Tooltip title="Open race">
                          <IconButton
                            component="a"
                            href={`/elections/${electionKey}/races/${race._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${officeName} race`}
                            size="small"
                            color="primary"
                          >
                            <ArrowForwardIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <OverviewDeleteButton
                        operations={[
                          group.raceOperation,
                          ...group.candidateOperations,
                          ...unmatchedCandidatesForRace
                        ]}
                        onDeleteOperations={onDeleteOperations}
                      />
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
        <Typography variant="body2" color="textSecondary">
          Showing {firstVisibleRace}–{lastVisibleRace} of {rows.length} races
        </Typography>
        {pageCount > 1 && (
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="small"
          />
        )}
      </div>
      {loadingOfficials && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="textSecondary">
            Loading official photos
          </Typography>
        </div>
      )}
    </div>
  );
};

type MeasureWithId = Measure & { _id: string };

const MeasureOperationsTable: React.FC<{
  operations: DataOperation[];
  operationType: OperationType;
  electionKey?: string;
  onDeleteOperations?: DeleteOverviewOperations;
}> = ({ operations, operationType, electionKey, onDeleteOperations }) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [measuresById, setMeasuresById] = useState<Map<string, MeasureWithId>>(new Map());
  const [districtsById, setDistrictsById] = useState<Map<string, District>>(new Map());
  const [loadingMeasures, setLoadingMeasures] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [measureLoadFailed, setMeasureLoadFailed] = useState(false);
  const [districtLoadFailed, setDistrictLoadFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedBallotTexts, setExpandedBallotTexts] = useState<Set<string>>(new Set());

  const measureIds = useMemo(() => {
    if (operationType === 'create') return [];
    return [...new Set(operations
      .map(operation => operation.resolvedId)
      .filter((id): id is string => Boolean(id)))];
  }, [operationType, operations]);

  useEffect(() => {
    setPage(1);
  }, [operations, operationType]);

  useEffect(() => {
    let cancelled = false;

    const loadMeasures = async () => {
      if (!feathers || measureIds.length === 0) {
        setMeasuresById(new Map());
        setMeasureLoadFailed(false);
        setLoadingMeasures(false);
        return;
      }

      setLoadingMeasures(true);
      setMeasureLoadFailed(false);
      try {
        const measures = await loadEntitiesByChunkedValues<Measure>(
          measureIds,
          async chunk => {
            const response = await feathers.getService('measures').find({
              query: { _id: chunk, $limit: ENTITY_QUERY_CHUNK_SIZE }
            });
            return response.data;
          }
        ) as MeasureWithId[];
        if (cancelled) return;
        setMeasuresById(new Map(measures.map(measure => [`${measure._id}`, measure])));
      } catch {
        if (cancelled) return;
        setMeasuresById(new Map());
        setMeasureLoadFailed(true);
      } finally {
        if (!cancelled) setLoadingMeasures(false);
      }
    };

    loadMeasures();
    return () => {
      cancelled = true;
    };
  }, [feathers, measureIds]);

  const districtIds = useMemo(() => {
    const ids = operations.map(operation => operation.data?.district);
    measuresById.forEach(measure => ids.push(measure.district));
    return [...new Set(ids
      .map(district => typeof district === 'string' ? district : `${district?._id || ''}`)
      .filter(Boolean))];
  }, [measuresById, operations]);

  useEffect(() => {
    let cancelled = false;

    const loadDistricts = async () => {
      if (!feathers || districtIds.length === 0) {
        setDistrictsById(new Map());
        setDistrictLoadFailed(false);
        setLoadingDistricts(false);
        return;
      }

      setLoadingDistricts(true);
      setDistrictLoadFailed(false);
      try {
        const districts = await loadEntitiesByChunkedValues<District>(
          districtIds,
          async chunk => {
            const response = await feathers.getService('districts').find({
              query: { _id: chunk, $limit: ENTITY_QUERY_CHUNK_SIZE }
            });
            return response.data;
          }
        );
        if (cancelled) return;
        setDistrictsById(new Map(districts.map(district => [`${district._id}`, district])));
      } catch {
        if (cancelled) return;
        setDistrictsById(new Map());
        setDistrictLoadFailed(true);
      } finally {
        if (!cancelled) setLoadingDistricts(false);
      }
    };

    loadDistricts();
    return () => {
      cancelled = true;
    };
  }, [districtIds, feathers]);

  const pageCount = Math.ceil(operations.length / MEASURES_PER_PAGE);
  const visibleOperations = operations.slice((page - 1) * MEASURES_PER_PAGE, page * MEASURES_PER_PAGE);
  const firstVisibleMeasure = operations.length === 0 ? 0 : (page - 1) * MEASURES_PER_PAGE + 1;
  const lastVisibleMeasure = Math.min(page * MEASURES_PER_PAGE, operations.length);
  const operationLabel = operationType === 'create'
    ? 'created'
    : operationType === 'delete'
      ? 'deleted'
      : 'updated';

  if (operations.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', backgroundColor: '#fafafa', borderRadius: '6px' }}>
        <Typography variant="body2" color="textSecondary">
          No measures will be {operationLabel}.
        </Typography>
      </div>
    );
  }

  return (
    <div>
      {measureLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          Existing measure details could not be loaded.
        </Typography>
      )}
      {districtLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          District names could not be loaded. District IDs are shown instead.
        </Typography>
      )}
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
          <thead style={{ backgroundColor: '#f7f8fa' }}>
            <tr>
              <th style={{ padding: '12px 16px', textAlign: 'left', width: '40%', color: '#4b5563' }}>Measure</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', width: '52%', color: '#4b5563' }}>Ballot text</th>
              {(operationType !== 'create' || onDeleteOperations) && (
                <th aria-label="Actions" style={{ padding: '12px 16px', width: '8%' }} />
              )}
            </tr>
          </thead>
          <tbody>
            {visibleOperations.map(operation => {
              const existingMeasure = operation.resolvedId
                ? measuresById.get(operation.resolvedId)
                : undefined;
              const districtValue = operation.data?.district ?? existingMeasure?.district;
              const populatedDistrict = typeof districtValue === 'object' ? districtValue as District : undefined;
              const districtId = typeof districtValue === 'string'
                ? districtValue
                : `${populatedDistrict?._id || ''}`;
              const district = populatedDistrict || districtsById.get(districtId);
              const districtName = district?.longName ||
                district?.displayName ||
                district?.name ||
                districtId ||
                'Unknown district';
              const title = operation.data?.title ?? existingMeasure?.title ?? operation.uniqueId;
              const updatedBallotText = operation.data?.ballotText;
              const ballotTextChanged = (
                operationType === 'update' &&
                existingMeasure !== undefined &&
                updatedBallotText !== undefined &&
                updatedBallotText !== existingMeasure.ballotText
              );
              const ballotText = operationType === 'update'
                ? ballotTextChanged
                  ? `Updating ballot text: "${updatedBallotText}"`
                  : 'No updates'
                : updatedBallotText ?? existingMeasure?.ballotText ?? '--';
              const clippedBallotText = clipWords(ballotText, 150);
              const ballotTextCanExpand = clippedBallotText !== ballotText;
              const ballotTextExpanded = expandedBallotTexts.has(operation.uniqueId);
              const measureUrl = operationType !== 'create' && operation.resolvedId && electionKey
                ? `/elections/${electionKey}/measures/${operation.resolvedId}`
                : null;

              return (
                <tr key={operation.uniqueId} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '18px 16px', verticalAlign: 'top' }}>
                    <Typography variant="subtitle1" style={{ color: '#172033', fontWeight: 600 }}>
                      {loadingMeasures && operationType !== 'create' && !existingMeasure
                        ? 'Loading measure…'
                        : title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {loadingDistricts && districtId && !district ? 'Loading district…' : districtName}
                    </Typography>
                  </td>
                  <td style={{ padding: '18px 16px', verticalAlign: 'top' }}>
                    {ballotTextCanExpand ? (
                      <Typography variant="body2" style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                        {ballotTextExpanded ? ballotText : clippedBallotText}{' '}
                        <Button
                          size="small"
                          aria-expanded={ballotTextExpanded}
                          onClick={() => {
                            setExpandedBallotTexts(current => {
                              const next = new Set(current);
                              if (next.has(operation.uniqueId)) {
                                next.delete(operation.uniqueId);
                              } else {
                                next.add(operation.uniqueId);
                              }
                              return next;
                            });
                          }}
                          style={{
                            minWidth: 'auto',
                            padding: 0,
                            verticalAlign: 'baseline',
                            textTransform: 'none'
                          }}
                        >
                          {ballotTextExpanded ? 'Show less' : 'Show full text'}
                        </Button>
                      </Typography>
                    ) : (
                      <Typography variant="body2" style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                        {ballotText}
                      </Typography>
                    )}
                  </td>
                  {(operationType !== 'create' || onDeleteOperations) && (
                    <td style={{ padding: '18px 16px', textAlign: 'right', verticalAlign: 'middle' }}>
                      {measureUrl && (
                        <Tooltip title="Open measure">
                          <IconButton
                            component="a"
                            href={measureUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${title} measure`}
                            size="small"
                            color="primary"
                          >
                            <ArrowForwardIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <OverviewDeleteButton
                        operations={[operation]}
                        onDeleteOperations={onDeleteOperations}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
        <Typography variant="body2" color="textSecondary">
          Showing {firstVisibleMeasure}–{lastVisibleMeasure} of {operations.length} measures
        </Typography>
        {pageCount > 1 && (
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="small"
          />
        )}
      </div>
      {(loadingMeasures || loadingDistricts) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="textSecondary">
            Loading measure details
          </Typography>
        </div>
      )}
    </div>
  );
};

interface ScoutingDistrictGroup {
  districtId: string;
  districtLongName?: string;
  contentTypes: Set<ContentType>;
  operations: DataOperation[];
}

const ScoutingOperationsTable: React.FC<{
  operations: DataOperation[];
  onDeleteOperations?: DeleteOverviewOperations;
}> = ({ operations, onDeleteOperations }) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [implementationDistrictsById, setImplementationDistrictsById] = useState<Map<string, ImplementationDistrict>>(new Map());
  const [districtsById, setDistrictsById] = useState<Map<string, District>>(new Map());
  const [loadingImplementationDistricts, setLoadingImplementationDistricts] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [implementationDistrictLoadFailed, setImplementationDistrictLoadFailed] = useState(false);
  const [districtLoadFailed, setDistrictLoadFailed] = useState(false);
  const [page, setPage] = useState(1);

  const implementationDistrictIds = useMemo(() => {
    return [...new Set(operations
      .map(operation => operation.resolvedId)
      .filter((id): id is string => Boolean(id)))];
  }, [operations]);

  useEffect(() => {
    setPage(1);
  }, [operations]);

  useEffect(() => {
    let cancelled = false;

    const loadImplementationDistricts = async () => {
      if (!feathers || implementationDistrictIds.length === 0) {
        setImplementationDistrictsById(new Map());
        setImplementationDistrictLoadFailed(false);
        setLoadingImplementationDistricts(false);
        return;
      }

      setLoadingImplementationDistricts(true);
      setImplementationDistrictLoadFailed(false);
      try {
        const implementationDistricts = await loadEntitiesByChunkedValues<ImplementationDistrict>(
          implementationDistrictIds,
          async chunk => {
            const response = await feathers.getService('implementation-districts').find({
              query: { _id: chunk, $limit: ENTITY_QUERY_CHUNK_SIZE }
            });
            return response.data;
          }
        );
        if (cancelled) return;
        setImplementationDistrictsById(new Map(
          implementationDistricts
            .filter(implementationDistrict => Boolean(implementationDistrict._id))
            .map(implementationDistrict => [`${implementationDistrict._id}`, implementationDistrict])
        ));
      } catch {
        if (cancelled) return;
        setImplementationDistrictsById(new Map());
        setImplementationDistrictLoadFailed(true);
      } finally {
        if (!cancelled) setLoadingImplementationDistricts(false);
      }
    };

    loadImplementationDistricts();
    return () => {
      cancelled = true;
    };
  }, [feathers, implementationDistrictIds]);

  const scoutingGroups = useMemo(() => {
    const groupsByDistrictId = new Map<string, ScoutingDistrictGroup>();

    operations.forEach(operation => {
      const implementationDistrict = operation.resolvedId
        ? implementationDistrictsById.get(operation.resolvedId)
        : undefined;
      if (!implementationDistrict?.district) return;

      const districtId = `${implementationDistrict.district}`;
      const existingGroup = groupsByDistrictId.get(districtId);
      if (existingGroup) {
        existingGroup.contentTypes.add(implementationDistrict.contentType);
        existingGroup.operations.push(operation);
        return;
      }

      groupsByDistrictId.set(districtId, {
        districtId,
        districtLongName: implementationDistrict.districtLongName,
        contentTypes: new Set([implementationDistrict.contentType]),
        operations: [operation]
      });
    });

    return [...groupsByDistrictId.values()];
  }, [implementationDistrictsById, operations]);

  const districtIds = useMemo(
    () => scoutingGroups.map(group => group.districtId),
    [scoutingGroups]
  );

  useEffect(() => {
    let cancelled = false;

    const loadDistricts = async () => {
      if (!feathers || districtIds.length === 0) {
        setDistrictsById(new Map());
        setDistrictLoadFailed(false);
        setLoadingDistricts(false);
        return;
      }

      setLoadingDistricts(true);
      setDistrictLoadFailed(false);
      try {
        const districts = await loadEntitiesByChunkedValues<District>(
          districtIds,
          async chunk => {
            const response = await feathers.getService('districts').find({
              query: { _id: chunk, $limit: ENTITY_QUERY_CHUNK_SIZE }
            });
            return response.data;
          }
        );
        if (cancelled) return;
        setDistrictsById(new Map(districts.map(district => [`${district._id}`, district])));
      } catch {
        if (cancelled) return;
        setDistrictsById(new Map());
        setDistrictLoadFailed(true);
      } finally {
        if (!cancelled) setLoadingDistricts(false);
      }
    };

    loadDistricts();
    return () => {
      cancelled = true;
    };
  }, [districtIds, feathers]);

  const orderedGroups = [...scoutingGroups].sort((first, second) => {
    const firstDistrict = districtsById.get(first.districtId);
    const secondDistrict = districtsById.get(second.districtId);
    const firstName = firstDistrict?.longName || first.districtLongName || first.districtId;
    const secondName = secondDistrict?.longName || second.districtLongName || second.districtId;
    return firstName.localeCompare(secondName);
  });
  const pageCount = Math.ceil(orderedGroups.length / SCOUTING_DISTRICTS_PER_PAGE);
  const visibleGroups = orderedGroups.slice(
    (page - 1) * SCOUTING_DISTRICTS_PER_PAGE,
    page * SCOUTING_DISTRICTS_PER_PAGE
  );
  const firstVisibleDistrict = orderedGroups.length === 0
    ? 0
    : (page - 1) * SCOUTING_DISTRICTS_PER_PAGE + 1;
  const lastVisibleDistrict = Math.min(page * SCOUTING_DISTRICTS_PER_PAGE, orderedGroups.length);
  const missingOperationCount = operations.length - [...implementationDistrictsById.values()]
    .filter(implementationDistrict => implementationDistrictIds.includes(`${implementationDistrict._id}`))
    .length;

  if (operations.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', backgroundColor: '#fafafa', borderRadius: '6px' }}>
        <Typography variant="body2" color="textSecondary">
          No scouting operations.
        </Typography>
      </div>
    );
  }

  if (loadingImplementationDistricts && scoutingGroups.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', padding: '40px' }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          Loading scouting operations
        </Typography>
      </div>
    );
  }

  return (
    <div>
      {implementationDistrictLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          Scouting operation details could not be loaded.
        </Typography>
      )}
      {!implementationDistrictLoadFailed && !loadingImplementationDistricts && missingOperationCount > 0 && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          {missingOperationCount} scouting operation{missingOperationCount === 1 ? '' : 's'} could not be found.
        </Typography>
      )}
      {districtLoadFailed && (
        <Typography variant="body2" color="error" style={{ marginBottom: '12px' }}>
          District names could not be loaded. District IDs are shown instead.
        </Typography>
      )}
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
          <thead style={{ backgroundColor: '#f7f8fa' }}>
            <tr>
              <th style={{ padding: '12px 16px', textAlign: 'left', width: '60%', color: '#4b5563' }}>District</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', width: '32%', color: '#4b5563' }}>Content type</th>
              <th aria-label="Actions" style={{ padding: '12px 16px', width: '8%' }} />
            </tr>
          </thead>
          <tbody>
            {visibleGroups.map(group => {
              const district = districtsById.get(group.districtId);
              const districtName = district?.longName ||
                district?.displayName ||
                district?.name ||
                group.districtLongName ||
                group.districtId;
              const hasRaces = group.contentTypes.has('races');
              const hasMeasures = group.contentTypes.has('measures');
              const contentType = hasRaces && hasMeasures
                ? 'Races and measures'
                : hasRaces
                  ? 'Races'
                  : 'Measures';

              return (
                <tr key={group.districtId} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '18px 16px', verticalAlign: 'middle' }}>
                    <Typography variant="subtitle1" style={{ color: '#172033', fontWeight: 600 }}>
                      {loadingDistricts && !district ? 'Loading district…' : districtName}
                    </Typography>
                  </td>
                  <td style={{ padding: '18px 16px', verticalAlign: 'middle' }}>
                    <Typography variant="body2" style={{ color: '#374151' }}>
                      {contentType}
                    </Typography>
                  </td>
                  <td style={{ padding: '18px 16px', textAlign: 'right', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {group.operations.map(operation => (
                        <OverviewDeleteButton
                          key={operation.uniqueId}
                          operations={[operation]}
                          onDeleteOperations={onDeleteOperations}
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
        <Typography variant="body2" color="textSecondary">
          Showing {firstVisibleDistrict}–{lastVisibleDistrict} of {orderedGroups.length} districts
        </Typography>
        {pageCount > 1 && (
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="small"
          />
        )}
      </div>
    </div>
  );
};

const getResolvedUrl = (operation: DataOperation, electionKey?: string, dataOperations?: DataOperation[]): string | null => {
  if (!operation.resolvedId || !electionKey) return null;

  if (operation.entityType === 'races') {
    return `/elections/${electionKey}/races/${operation.resolvedId}`;
  } else if (operation.entityType === 'candidates') {
    // Try to get raceId from data fields
    let raceId = operation.data?.raceId;
    if(raceId?.resolveTo) { 
      const resolvedRaceId = dataOperations?.find(op => op.uniqueId === raceId.resolveTo && op.entityType === 'races')?.resolvedId;
      if(resolvedRaceId) {
        raceId = resolvedRaceId;
      }
    }
    if (!raceId) return null;
    return `/elections/${electionKey}/races/${raceId}/candidates/${operation.resolvedId}?source=data-editor`;
  } else if (operation.entityType === 'measures') {
    return `/elections/${electionKey}/measures/${operation.resolvedId}`;
  }
  return null;
};

const FixOperationPopover: React.FC<FixOperationPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  onConfirm,
}) => {
  const [resolvedId, setResolvedId] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!resolvedId) {
      setError('Resolved ID is required');
      return;
    }
    if (!isValidObjectId(resolvedId)) {
      setError('Invalid MongoDB ObjectId format (must be 24 character hex string)');
      return;
    }
    onConfirm(resolvedId);
    onClose();
    setResolvedId('');
    setError('');
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <div style={{ padding: '16px', maxWidth: '300px' }}>
        <Typography variant="body2" style={{ marginBottom: '16px' }}>
          Please provide the resolved MongoDB ObjectId for this operation. This indicates that you have manually resolved the issue.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Resolved ID"
          value={resolvedId}
          onChange={(e) => {
            setResolvedId(e.target.value);
            setError('');
          }}
          error={!!error}
          helperText={error}
          style={{ marginBottom: '16px' }}
          placeholder="24 character hex string"
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button
            size="small"
            onClick={() => {
              onClose();
              setResolvedId('');
              setError('');
            }}
            style={{ color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          >
            Set as Fixed
          </Button>
        </div>
      </div>
    </Popover>
  );
};

const DataOperationsTable: React.FC<DataOperationsTableProps> = ({ 
  dataOperations, 
  title = "Data Operations Queue",
  onChangeDataOperations,
  onChangeDataOperation,
  onChangeOfficialMatch,
  electionKey,
  editingDisabled = false,
  editableJson = false,
  onRetryFailedOperations,
  defaultToOperationsTab = false,
  operationsOnly = false
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [statusFilter, setStatusFilter] = useState<StatusType>('all');
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedOperationId, setSelectedOperationId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [overviewTab, setOverviewTab] = useState<OverviewTab>('race-update');
  
  useEffect(() => {
    if (defaultToOperationsTab) {
      setActiveTab(1);
    }
  }, [defaultToOperationsTab]);

  const canEdit = !editingDisabled;
  const canEditJson = Boolean(onChangeDataOperations || onChangeDataOperation)
    && (canEdit || editableJson);
  const canChangeOfficialMatches = canEdit &&
    Boolean(onChangeOfficialMatch) &&
    (dataOperations || []).every(operation => ['not-started', 'no-op'].includes(operation.status));
  const pendingOfficialMatchChange = canChangeOfficialMatches
    ? onChangeOfficialMatch
    : undefined;

  const overviewOperations = useMemo<Record<OperationType, OverviewOperationGroup>>(() => {
    const groupedOperations: Record<OperationType, OverviewOperationGroup> = {
      update: { raceGroups: [], unmatchedCandidateOperations: [], operationCount: 0 },
      delete: { raceGroups: [], unmatchedCandidateOperations: [], operationCount: 0 },
      create: { raceGroups: [], unmatchedCandidateOperations: [], operationCount: 0 }
    };
    const raceGroupsByReference = new Map<string, RaceOperationGroup>();

    (dataOperations || []).forEach(operation => {
      if (operation.entityType !== 'races') return;
      const overviewType = getOverviewOperationType(operation);
      if (!overviewType) return;

      const raceGroup: RaceOperationGroup = {
        raceOperation: operation,
        overviewType,
        candidateOperations: []
      };
      const operationGroup = groupedOperations[overviewType];

      operationGroup.raceGroups.push(raceGroup);
      operationGroup.operationCount += 1;
      raceGroupsByReference.set(operation.uniqueId, raceGroup);
      if (operation.resolvedId) {
        raceGroupsByReference.set(operation.resolvedId, raceGroup);
      }
    });

    (dataOperations || []).forEach(operation => {
      if (operation.entityType !== 'candidates') return;
      const overviewType = getOverviewOperationType(operation);
      if (!overviewType) return;

      const raceReference = operation.data?.race;
      const resolveTo = raceReference?.resolveTo;
      const referencedRaceId = typeof raceReference === 'string'
        ? raceReference
        : typeof resolveTo === 'string'
          ? resolveTo
          : resolveTo?.uniqueId;
      const raceGroup = referencedRaceId
        ? raceGroupsByReference.get(referencedRaceId)
        : undefined;

      if (raceGroup) {
        raceGroup.candidateOperations.push(operation);
        groupedOperations[raceGroup.overviewType].operationCount += 1;
      } else {
        const operationGroup = groupedOperations[overviewType];
        operationGroup.unmatchedCandidateOperations.push(operation);
        operationGroup.operationCount += 1;
      }
    });

    return groupedOperations;
  }, [dataOperations]);

  const measureOperations = useMemo<Record<OperationType, DataOperation[]>>(() => {
    const groupedOperations: Record<OperationType, DataOperation[]> = {
      update: [],
      delete: [],
      create: []
    };

    (dataOperations || []).forEach(operation => {
      if (operation.entityType !== 'measures') return;
      const overviewType = getOverviewOperationType(operation);
      if (overviewType) groupedOperations[overviewType].push(operation);
    });

    return groupedOperations;
  }, [dataOperations]);

  const scoutingOperations = useMemo(
    () => (dataOperations || []).filter(operation => operation.entityType === 'implementation-districts'),
    [dataOperations]
  );

  // Toggle row expansion
  const toggleRowExpansion = (index: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleFixOperation = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setPopoverAnchorEl(event.currentTarget);
    setSelectedOperationId(id);
  };
  const handleConfirmFix = (resolvedId: string) => {
    if (selectedOperationId !== null && dataOperations && onChangeDataOperations) {
      const updatedOperations = dataOperations.map(op => {
        if (op.uniqueId === selectedOperationId) {
          return {
            ...op,
            status: 'corrected' as const,
            resolvedId
          };
        }
        return op;
      });
      onChangeDataOperations(updatedOperations);
    }
    setPopoverAnchorEl(null);
    setSelectedOperationId(null);
  };
  const handleDeleteOverviewOperations: DeleteOverviewOperations = operationsToDelete => {
    if (!dataOperations || !onChangeDataOperations) return;

    const operationsSet = new Set(operationsToDelete);
    setExpandedRows(new Set());
    onChangeDataOperations(dataOperations.filter(operation => !operationsSet.has(operation)));
  };
  const deleteOverviewOperations = canEdit && onChangeDataOperations
    ? handleDeleteOverviewOperations
    : undefined;

  if (!dataOperations || dataOperations.length === 0) {
    return (
      <div style={{ 
        padding: '16px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        textAlign: 'center'
      }}>
        <Typography variant="body2" color="textSecondary">
          No data operations available.
        </Typography>
      </div>
    );
  }

  const filteredOperationRows = dataOperations
    .map((operation, originalIndex) => ({ operation, originalIndex }))
    .filter(({ operation }) => statusFilter === 'all' || operation.status === statusFilter);

  const handleOperationDataChange = (originalIndex: number, updatedData: Record<string, any>) => {
    if (!dataOperations) return;

    const updatedOperations = dataOperations.map((op, index) => {
      if (index === originalIndex) {
        return {
          ...op,
          data: updatedData
        };
      }
      return op;
    });

    if (onChangeDataOperation) {
      onChangeDataOperation(updatedOperations[originalIndex]);
    } else {
      onChangeDataOperations?.(updatedOperations);
    }
  };

  const handleDataIdempotencyChange = (
    originalIndex: number,
    operation: DataOperation,
    enabled: boolean
  ) => {
    const dataWithoutIdempotency = { ...(operation.data || {}) };
    delete dataWithoutIdempotency[DATA_IDEMPOTENCY_FIELD];

    handleOperationDataChange(originalIndex, enabled
      ? {
        ...dataWithoutIdempotency,
        [DATA_IDEMPOTENCY_FIELD]: {
          ...operation,
          data: dataWithoutIdempotency
        }
      }
      : dataWithoutIdempotency
    );
  };

  const handleDeleteOperation = (originalIndex: number) => {
    if (!dataOperations || !onChangeDataOperations) return;

    const updatedOperations = dataOperations.filter((_, index) => index !== originalIndex);
    setExpandedRows(new Set());
    onChangeDataOperations(updatedOperations);
  };

  const noOpCount = dataOperations.filter(op => op.status === 'no-op').length;
  const updateOpCount = dataOperations.filter(op => op.operationType === 'update' && op.status !== 'no-op').length;
  const createOpCount = dataOperations.filter(op => op.operationType === 'create' && op.status !== 'no-op').length;
  const deleteOpCount = dataOperations.filter(op => op.operationType === 'delete' && op.status !== 'no-op').length;

  return (
    <div style={{ 
      padding: '24px',
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      marginBottom: '24px'
    }}>
      {!operationsOnly && (
        <Tabs
          value={activeTab}
          onChange={(_, newValue: number) => setActiveTab(newValue)}
          aria-label="Data operations views"
          style={{ marginBottom: '24px', borderBottom: '1px solid #e0e0e0' }}
        >
          <Tab label="Overview" id="data-operations-tab-0" aria-controls="data-operations-tabpanel-0" />
          <Tab label="Operations" id="data-operations-tab-1" aria-controls="data-operations-tabpanel-1" />
        </Tabs>
      )}

      {!operationsOnly && activeTab === 0 && (
        <div
          role="tabpanel"
          id="data-operations-tabpanel-0"
          aria-labelledby="data-operations-tab-0"
        >
          <Tabs
            value={overviewTab}
            onChange={(_, newValue: OverviewTab) => setOverviewTab(newValue)}
            aria-label="Race and measure operation types"
            variant="scrollable"
            scrollButtons="auto"
            style={{ marginBottom: '16px' }}
          >
            <Tab
              label={`Race Updates (${overviewOperations.update.raceGroups.length})`}
              value="race-update"
              id="race-operations-tab-update"
              aria-controls="race-operations-tabpanel-update"
            />
            <Tab
              label={`Race Deletes (${overviewOperations.delete.raceGroups.length})`}
              value="race-delete"
              id="race-operations-tab-delete"
              aria-controls="race-operations-tabpanel-delete"
            />
            <Tab
              label={`Race Creates (${overviewOperations.create.raceGroups.length})`}
              value="race-create"
              id="race-operations-tab-create"
              aria-controls="race-operations-tabpanel-create"
            />
            <Tab
              label={`Measure Updates (${measureOperations.update.length})`}
              value="measure-update"
              id="measure-operations-tab-update"
              aria-controls="measure-operations-tabpanel-update"
            />
            <Tab
              label={`Measure Deletes (${measureOperations.delete.length})`}
              value="measure-delete"
              id="measure-operations-tab-delete"
              aria-controls="measure-operations-tabpanel-delete"
            />
            <Tab
              label={`Measure Creates (${measureOperations.create.length})`}
              value="measure-create"
              id="measure-operations-tab-create"
              aria-controls="measure-operations-tabpanel-create"
            />
            <Tab
              label={`Scouting (${scoutingOperations.length})`}
              value="scouting"
              id="scouting-operations-tab"
              aria-controls="scouting-operations-tabpanel"
            />
          </Tabs>
          {overviewTab === 'race-create' && (
            <RaceCreatesTable
              raceGroups={overviewOperations.create.raceGroups}
              onChangeOfficialMatch={pendingOfficialMatchChange}
              onDeleteOperations={deleteOverviewOperations}
            />
          )}
          {overviewTab === 'race-delete' && (
            <RaceDeletesTable
              raceGroups={overviewOperations.delete.raceGroups}
              electionKey={electionKey}
              onDeleteOperations={deleteOverviewOperations}
            />
          )}
          {overviewTab === 'race-update' && (
            <RaceUpdatesTable
              raceGroups={overviewOperations.update.raceGroups}
              unmatchedCandidateOperations={[
                ...overviewOperations.update.unmatchedCandidateOperations,
                ...overviewOperations.delete.unmatchedCandidateOperations
              ]}
              electionKey={electionKey}
              onChangeOfficialMatch={pendingOfficialMatchChange}
              onDeleteOperations={deleteOverviewOperations}
            />
          )}
          {overviewTab === 'measure-create' && (
            <MeasureOperationsTable
              operations={measureOperations.create}
              operationType="create"
              onDeleteOperations={deleteOverviewOperations}
            />
          )}
          {overviewTab === 'measure-update' && (
            <MeasureOperationsTable
              operations={measureOperations.update}
              operationType="update"
              electionKey={electionKey}
              onDeleteOperations={deleteOverviewOperations}
            />
          )}
          {overviewTab === 'measure-delete' && (
            <MeasureOperationsTable
              operations={measureOperations.delete}
              operationType="delete"
              electionKey={electionKey}
              onDeleteOperations={deleteOverviewOperations}
            />
          )}
          {overviewTab === 'scouting' && (
            <ScoutingOperationsTable
              operations={scoutingOperations}
              onDeleteOperations={deleteOverviewOperations}
            />
          )}
        </div>
      )}
      
      {(operationsOnly || activeTab === 1) && (
        <div
          role="tabpanel"
          id="data-operations-tabpanel-1"
          aria-labelledby="data-operations-tab-1"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <Typography variant="h6" style={{ fontWeight: '600' }}>
                {title} ({filteredOperationRows.length} operations)
              </Typography>
              <Typography variant="body2" style={{ fontWeight: '600' }}>
                {noOpCount} no-ops, {updateOpCount} updates, {createOpCount} creates, {deleteOpCount} deletes
              </Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TypeDropdown
                value={statusFilter}
                options={[
                  { type: 'all', title: 'All Statuses' },
                  { type: 'not-started', title: 'Not Started' },
                  { type: 'completed', title: 'Completed' },
                  { type: 'failed', title: 'Failed' },
                  { type: 'no-op', title: 'No Operation' },
                  { type: 'corrected', title: 'Fixed' }
                ]}
                onChange={(newStatus: StatusType) => setStatusFilter(newStatus)}
                placeholder="Select Status"
              />
              {onRetryFailedOperations && dataOperations && dataOperations.some(op => ['failed', 'not-started'].includes(op.status)) && (
                <Tooltip title="Retry pending operations">
                  <IconButton
                    size="small"
                    onClick={onRetryFailedOperations}
                    color="primary"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>

          <OperationsProgress dataOperations={dataOperations} />

          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ 
            backgroundColor: '#f5f5f5', 
            position: 'sticky', 
            top: 0, 
            zIndex: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <tr>
              <th style={{
                padding: '12px 8px',
                textAlign: 'left',
                borderBottom: '1px solid #ddd',
                fontSize: '14px',
                fontWeight: 'bold',
                width: '15%'
              }}>
                Service Type
              </th>
              <th style={{ 
                padding: '12px 8px', 
                textAlign: 'left', 
                borderBottom: '1px solid #ddd',
                fontSize: '14px',
                fontWeight: 'bold',
                width: '20%'
              }}>
                Identifier
              </th>
              <th style={{ 
                padding: '12px 8px', 
                textAlign: 'left', 
                borderBottom: '1px solid #ddd',
                fontSize: '14px',
                fontWeight: 'bold',
                width: '15%'
              }}>
                Operation
              </th>
              <th style={{ 
                padding: '12px 8px', 
                textAlign: 'left', 
                borderBottom: '1px solid #ddd',
                fontSize: '14px',
                fontWeight: 'bold',
                width: '30%'
              }}>
                Data Preview
              </th>
              <th style={{ 
                padding: '12px 8px', 
                textAlign: 'left', 
                borderBottom: '1px solid #ddd',
                fontSize: '14px',
                fontWeight: 'bold',
                width: '20%'
              }}>
                Status
              </th>
              <th style={{
                padding: '12px 8px',
                textAlign: 'left',
                borderBottom: '1px solid #ddd',
                fontSize: '14px',
                fontWeight: 'bold',
                width: '5%'
              }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredOperationRows.map(({ operation, originalIndex }, filteredIndex) => {
              const isExpanded = expandedRows.has(originalIndex);
              const fieldCount = Object.keys(operation.data || {}).length;
              const operationText = getOperationText(operation.operationType);
              const dataPreviewText = operation.operationType === 'create'
                ? `Create with ${fieldCount} fields`
                : operation.operationType === 'delete'
                  ? 'Delete existing record'
                  : operation.operationType === 'no-op'
                    ? 'No changes'
                    : `Update ${fieldCount} fields`;
              const operationColor = operation.operationType === 'create'
                ? { background: '#e3f2fd', text: '#1976d2', border: '#bbdefb' }
                : operation.operationType === 'delete'
                  ? { background: '#ffebee', text: '#d32f2f', border: '#ffcdd2' }
                  : { background: '#fff3e0', text: '#f57c00', border: '#ffcc80' };
              
              // Get status color and text
              const getStatusInfo = (status: string) => {
                switch (status) {
                  case 'not-started':
                    return { color: '#6c757d', text: 'Not Started', bgColor: '#f8f9fa' };
                  case 'completed':
                    return { color: '#28a745', text: 'Completed', bgColor: '#e8f5e8' };
                  case 'failed':
                    return { color: '#dc3545', text: 'Failed', bgColor: '#ffebee' };
                  case 'no-op':
                    return { color: '#28a745', text: 'No Operation', bgColor: '#e8f5e8' };
                  case 'corrected':
                    return { color: '#2e7d32', text: 'Fixed', bgColor: '#e0f2e0' };
                  default:
                    return { color: '#6c757d', text: status, bgColor: '#f8f9fa' };
                }
              };
              
              const statusInfo = getStatusInfo(operation.status);
              const canDeleteOperation = canEdit && operation.status !== 'completed';
              
              return (
                <React.Fragment key={originalIndex}>
                  <tr style={{ backgroundColor: filteredIndex % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{
                      padding: '12px 8px',
                      borderBottom: '1px solid #eee', 
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      {operation.entityType}
                    </td>
                    <td style={{ 
                      padding: '12px 8px', 
                      borderBottom: '1px solid #eee', 
                      fontFamily: 'monospace',
                      fontSize: '12px'
                    }}>
                      {operation.uniqueId}
                    </td>
                    <td style={{ 
                      padding: '12px 8px', 
                      borderBottom: '1px solid #eee', 
                      fontSize: '14px'
                    }}>
                      <span style={{ 
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: operationColor.background,
                        color: operationColor.text,
                        border: `1px solid ${operationColor.border}`
                      }}>
                        {operationText}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '12px 8px', 
                      borderBottom: '1px solid #eee', 
                      fontSize: '14px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', color: '#666' }}>
                          {dataPreviewText}
                        </span>
                        <Button
                          onClick={() => toggleRowExpansion(originalIndex)}
                          style={{ 
                            minWidth: 'auto', 
                            padding: '4px',
                            color: '#666',
                            fontSize: '12px'
                          }}
                          size="small"
                        >
                          {isExpanded ? 'Hide' : 'View'}
                        </Button>
                      </div>
                    </td>
                    <td 
                      style={{ 
                        padding: '12px 8px', 
                        borderBottom: '1px solid #eee', 
                        fontSize: '14px',
                        cursor: operation.status === 'failed' ? 'pointer' : 'default'
                      }}
                      onClick={operation.status === 'failed' ? (e) => handleFixOperation(e, operation.uniqueId) : undefined}
                    > 
                      <Tooltip 
                        title={operation.status === 'failed' && operation.errorMessage ? operation.errorMessage : ''} 
                        placement="top"
                        disableHoverListener={operation.status !== 'failed' || !operation.errorMessage}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span 
                            style={{ 
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: statusInfo.bgColor,
                              color: statusInfo.color,
                              border: `1px solid ${statusInfo.color}40`,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            {statusInfo.text}
                          </span>
                          {operation.status === 'failed' && (
                            <span style={{ marginLeft: '4px', display: 'inline-flex', alignItems: 'center' }}>
                              <BuildIcon style={{ color: '#666', fontSize: '14px' }} />
                            </span>
                          )}
                          {operation.resolvedId && getResolvedUrl(operation, electionKey, dataOperations) && (
                            <Tooltip title="Open in new tab">
                              <a
                                href={getResolvedUrl(operation, electionKey, dataOperations) || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ 
                                  marginLeft: '8px', 
                                  display: 'inline-flex', 
                                  alignItems: 'center',
                                  color: '#1976d2'
                                }}
                              >
                                <OpenInNewIcon style={{ fontSize: '14px' }} />
                              </a>
                            </Tooltip>
                          )}
                        </div>
                      </Tooltip>
                    </td>
                    <td style={{
                      padding: '12px 8px',
                      borderBottom: '1px solid #eee',
                      fontSize: '14px'
                    }}>
                      {canDeleteOperation && (
                        <Tooltip title="Delete operation">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteOperation(originalIndex)}
                            color="error"
                          >
                            <DeleteIcon style={{ fontSize: '16px' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={6} style={{
                        padding: '0',
                        borderBottom: '1px solid #eee',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <div style={{ padding: '16px', margin: '0 12px' }}>
                          <JSONEditor<Record<string, any>>
                            value={operation.data || {}}
                            onChange={(updatedData) => handleOperationDataChange(originalIndex, updatedData)}
                            editable={canEditJson}
                            saveOnBlur={false}
                            title="Operation Details"
                            titleAction={operation.entityType === 'races' ? (
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="caption" color="textSecondary">
                                  Idempotency
                                </Typography>
                                <Switch
                                  size="small"
                                  checked={Object.prototype.hasOwnProperty.call(
                                    operation.data || {},
                                    DATA_IDEMPOTENCY_FIELD
                                  )}
                                  onChange={(_, checked) => {
                                    handleDataIdempotencyChange(originalIndex, operation, checked);
                                  }}
                                  disabled={!canEdit}
                                  inputProps={{ 'aria-label': 'Use data operation idempotency' }}
                                />
                              </div>
                            ) : undefined}
                          />
                          {operation.errorMessage && (
                            <div style={{ 
                              marginTop: '8px',
                              padding: '8px 12px',
                              backgroundColor: '#ffebee',
                              border: '1px solid #ffcdd2',
                              borderRadius: '4px'
                            }}>
                              <Typography variant="caption" style={{ color: '#d32f2f', fontWeight: '500' }}>
                                Error: {operation.errorMessage}
                              </Typography>
                            </div>
                          )}
                          {operation.resolvedId && (
                            <div style={{ 
                              marginTop: '8px',
                              padding: '8px 12px',
                              backgroundColor: '#e8f5e8',
                              border: '1px solid #c8e6c9',
                              borderRadius: '4px'
                            }}>
                              <Typography variant="caption" style={{ color: '#2e7d32', fontWeight: '500' }}>
                                Resolved ID: {operation.resolvedId}
                              </Typography>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
            </table>
          </div>
          <FixOperationPopover
            open={Boolean(popoverAnchorEl)}
            anchorEl={popoverAnchorEl}
            onClose={() => {
              setPopoverAnchorEl(null);
              setSelectedOperationId(null);
            }}
            onConfirm={handleConfirmFix}
          />
        </div>
      )}
    </div>
  );
};

export default DataOperationsTable; 